import React from 'react';

import Heading from '@atlaskit/heading';
import ChatIcon from '@atlaskit/icon/glyph/comment';
import NotificationDirect from '@atlaskit/icon/glyph/notification-direct';
import PeopleIcon from '@atlaskit/icon/glyph/people-group';
import PersonIcon from '@atlaskit/icon/glyph/person';
import WatchIcon from '@atlaskit/icon/glyph/watch';
import { Flex, Stack, Text } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import { useIsAdmin, useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller';
import { IconBox, IconBoxColor } from '../../../../common/ui/icon-box';
import { useChatToolsIntegrations } from '../../../../services/store/chat-tools';
import {
	useGetUsersWithoutAdminRole,
	useGetUsersWithoutCustomRole,
} from '../../../../services/store/users-and-roles';
import { List } from './layout';
import { messages } from './messages';

const ListItem = ({ children }: { children: React.ReactNode }) => (
	<Flex justifyContent="start" columnGap="space.075" alignItems="start" role="listitem">
		{children}
	</Flex>
);

export const StepFive = ({ isFirstUnlocked = true }: { isFirstUnlocked?: boolean }) => {
	const isAdmin = useIsAdmin();
	const isSiteAdmin = useIsSiteAdmin();
	const isUser = !isAdmin && !isSiteAdmin;
	const [usersWithoutAdminRole] = useGetUsersWithoutAdminRole();
	const [usersWithoutCustomRole] = useGetUsersWithoutCustomRole();
	const [integrations] = useChatToolsIntegrations();

	const { formatMessage } = useIntl();

	if (isUser) {
		return (
			<List
				heading={formatMessage(messages.titleByUser)}
				description={<Text>{formatMessage(messages.listDescriptionForAnotherAdmin)}</Text>}
			>
				<Stack space="space.200">
					<Heading size="xsmall" as="h2">
						{formatMessage(messages.listItemHeading)}
					</Heading>
					<Stack space="space.100">
						<ListItem>
							<IconBox icon={NotificationDirect} color={IconBoxColor.Orange} />
							{formatMessage(messages.listItemOneUser)}
						</ListItem>
						<ListItem>
							<IconBox icon={WatchIcon} color={IconBoxColor.Green} />
							{formatMessage(messages.listItemTwoUser)}
						</ListItem>
					</Stack>
				</Stack>
			</List>
		);
	}

	if (isFirstUnlocked) {
		return (
			<List
				heading={formatMessage(messages.titleUnlockedBySameAdmin)}
				description={
					<Stack space="space.250">
						<Text>{formatMessage(messages.listDescription)}</Text>
						<Text>{formatMessage(messages.listDescriptionTwo)}</Text>
					</Stack>
				}
			/>
		);
	}

	return (
		<List
			heading={formatMessage(messages.titleUnlockedByAnotherAdmin)}
			description={<Text>{formatMessage(messages.listDescriptionForAnotherAdmin)}</Text>}
		>
			<Stack space="space.200">
				<Heading size="xsmall" as="h2">
					{formatMessage(messages.listItemHeading)}
				</Heading>
				<Stack space="space.100">
					{integrations.data?.count !== 0 && (
						<ListItem>
							<IconBox icon={ChatIcon} color={IconBoxColor.Teal} />
							{formatMessage(messages.listItemOne)}
						</ListItem>
					)}
					{usersWithoutAdminRole.data?.count !== 0 && (
						<ListItem>
							<IconBox icon={PersonIcon} color={IconBoxColor.Orange} />
							{formatMessage(messages.listItemTwo, {
								userAmount: usersWithoutAdminRole.data?.values.length ?? 0,
							})}
						</ListItem>
					)}
					{usersWithoutCustomRole.data?.count !== 0 && (
						<ListItem>
							<IconBox icon={PeopleIcon} color={IconBoxColor.Purple} />
							{formatMessage(messages.listItemThree, {
								userAmount: usersWithoutCustomRole.data?.values.length ?? 0,
							})}
						</ListItem>
					)}
				</Stack>
			</Stack>
		</List>
	);
};
